import { BusinessForm } from "@nwga/features/businesses/ui";
import { useTRPC } from "@nwga/features/client";
import { Button } from "@nwga/ui/components/button";
import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	redirect,
	useNavigate,
} from "@tanstack/react-router";
import { ClipboardCheckIcon, MailIcon, RocketIcon } from "lucide-react";

/**
 * Registering a business.
 *
 * Its own route rather than a panel that expands on the dashboard: this is the
 * single most consequential form in the product — it starts a review by a real
 * person — and giving it a URL means it can be linked from an email, from the
 * marketing page, and from the empty dashboard, and that a half-filled form
 * survives a refresh of the page it is actually on.
 *
 * What happens next is stated *before* the form rather than after it. Somebody
 * deciding whether to spend five minutes on this wants to know it will be read
 * by a person and roughly when.
 */
export const Route = createFileRoute("/businesses/register")({
	beforeLoad: async ({ context, location }) => {
		const session = await context.queryClient.ensureQueryData(
			context.trpc.session.queryOptions(),
		);

		if (session.user === null) {
			// Sign-up rather than sign-in: somebody arriving here from the marketing
			// page almost certainly has no account yet, and the sign-up page links
			// to sign-in for the minority who do.
			throw redirect({ to: "/sign-up", search: { redirect: location.href } });
		}
	},
	component: RegisterBusinessPage,
});

const STEPS = [
	{
		icon: ClipboardCheckIcon,
		title: "Tell us about the business",
		body: "Name, where it operates, and how people can reach you. Two minutes.",
	},
	{
		icon: MailIcon,
		title: "A JDA reviewer reads it",
		body: "Usually within two business days. We'll email you either way, and if anything needs changing we'll say exactly what.",
	},
	{
		icon: RocketIcon,
		title: "Post your openings",
		body: "You can draft listings straight away — they go live the moment you're approved.",
	},
] as const;

function RegisterBusinessPage() {
	const navigate = useNavigate();
	const trpc = useTRPC();
	const mine = useQuery(trpc.businesses.mine.queryOptions());

	return (
		<div className="mx-auto max-w-2xl px-4 py-10">
			{(mine.data?.length ?? 0) > 0 && (
				<Button asChild variant="ghost" size="sm" className="-ml-2 mb-4">
					<Link to="/dashboard">← Your businesses</Link>
				</Button>
			)}

			<h1 className="font-semibold text-2xl tracking-tight">
				Register your business
			</h1>
			<p className="mt-1 text-muted-foreground text-pretty">
				Businesses in Catoosa, Chattooga, Dade and Walker counties can post
				openings to the board free of charge.
			</p>

			<ol className="mt-8 space-y-4">
				{STEPS.map((step, index) => (
					<li key={step.title} className="flex gap-4">
						<div className="flex size-9 shrink-0 items-center justify-center rounded-full border bg-card">
							<step.icon
								className="size-4 text-muted-foreground"
								aria-hidden="true"
							/>
						</div>
						<div className="min-w-0 pt-1">
							<p className="font-medium text-sm">
								<span className="text-muted-foreground">{index + 1}. </span>
								{step.title}
							</p>
							<p className="mt-0.5 text-muted-foreground text-sm text-pretty">
								{step.body}
							</p>
						</div>
					</li>
				))}
			</ol>

			<div className="mt-10 rounded-lg border bg-card p-6">
				<BusinessForm
					onSaved={(business) => {
						void navigate({
							to: "/businesses/registered/$businessSlug",
							params: { businessSlug: business.slug },
						});
					}}
				/>
			</div>
		</div>
	);
}
